
import React from 'react';

const AutumnHayFeverGuidePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-gradient-to-r from-orange-400 to-red-500 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mr-4">
              <i className="ri-leaf-line text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">秋の花粉症対策｜春との違いとおすすめの薬・ケア方法</h1>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="container mx-auto px-4 py-12">
        <article className="max-w-4xl mx-auto">
          {/* 導入部 */}
          <section className="mb-12">
            <div className="bg-orange-50 border-l-4 border-orange-400 p-6 rounded-r-lg mb-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                秋になると目のかゆみやくしゃみが気になる方が増えています。春の花粉症とは原因植物や症状の特徴が異なる秋の花粉症について、薬剤師が効果的な対策方法と薬の選び方を詳しく解説します。
              </p>
            </div>
          </section>

          {/* 秋の花粉症の特徴 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-400 pb-2">
              <i className="ri-leaf-line text-orange-500 mr-2"></i>
              秋の花粉症の特徴
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-amber-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  <i className="ri-calendar-line text-amber-600 mr-2"></i>
                  発症時期
                </h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• 8月下旬～10月頃がピーク</li>
                  <li>• 9月中旬に最も症状が強くなる</li>
                  <li>• 地域により若干の差がある</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  <i className="ri-plant-line text-green-600 mr-2"></i>
                  主な原因植物
                </h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• ブタクサ（最も多い）</li>
                  <li>• ヨモギ</li>
                  <li>• セイタカアワダチソウ</li>
                  <li>• オオアワガエリ</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                <i className="ri-eye-line text-blue-600 mr-2"></i>
                秋の花粉症の症状
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">鼻の症状</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• くしゃみ</li>
                    <li>• 鼻水・鼻づまり</li>
                    <li>• 鼻のかゆみ</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">目の症状</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 目のかゆみ</li>
                    <li>• 涙が出る</li>
                    <li>• 目の充血</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">その他</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• のどのイガイガ</li>
                    <li>• 皮膚のかゆみ</li>
                    <li>• 咳</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 春の花粉症との違い */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-400 pb-2">
              <i className="ri-contrast-line text-orange-500 mr-2"></i>
              春の花粉症との違い
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 bg-white rounded-lg overflow-hidden shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">項目</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-orange-600">秋の花粉症</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-green-600">春の花粉症</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-medium">原因植物</td>
                    <td className="border border-gray-300 px-4 py-3">ブタクサ、ヨモギなど</td>
                    <td className="border border-gray-300 px-4 py-3">スギ、ヒノキなど</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-medium">花粉の特徴</td>
                    <td className="border border-gray-300 px-4 py-3">重く、飛距離が短い</td>
                    <td className="border border-gray-300 px-4 py-3">軽く、広範囲に飛散</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-medium">症状の強さ</td>
                    <td className="border border-gray-300 px-4 py-3">比較的軽症が多い</td>
                    <td className="border border-gray-300 px-4 py-3">重症化しやすい</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-medium">対策のポイント</td>
                    <td className="border border-gray-300 px-4 py-3">河川敷・空き地を避ける</td>
                    <td className="border border-gray-300 px-4 py-3">屋外活動全般を控える</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* おすすめの薬 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-400 pb-2">
              <i className="ri-capsule-line text-orange-500 mr-2"></i>
              秋の花粉症におすすめの薬
            </h2>
            
            <div className="space-y-8">
              {/* 抗ヒスタミン薬 */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-800 mb-4">
                  <i className="ri-medicine-bottle-line text-blue-600 mr-2"></i>
                  抗ヒスタミン薬（第2世代）
                </h3>
                <p className="text-gray-700 mb-4">秋の花粉症対策の第一選択薬として推奨</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-semibold text-gray-800 mb-2">セチリジン系</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• ジルテック、コンタック鼻炎Z</li>
                      <li>• 効果が強く、眠気が少ない</li>
                      <li>• 1日1回服用</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-semibold text-gray-800 mb-2">ロラタジン系</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• クラリチン、ロラータ</li>
                      <li>• 眠気が最も少ない</li>
                      <li>• 1日1回服用</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 点鼻薬 */}
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-green-800 mb-4">
                  <i className="ri-drop-line text-green-600 mr-2"></i>
                  ステロイド点鼻薬
                </h3>
                <p className="text-gray-700 mb-4">鼻づまりが強い場合に効果的</p>
                
                <div className="bg-white p-4 rounded border">
                  <ul className="text-gray-700 space-y-2">
                    <li>• <strong>フルナーゼ</strong>：効果が高く、使いやすい</li>
                    <li>• <strong>エリザス</strong>：1日1回で効果持続</li>
                    <li>• <strong>アレジオン点鼻</strong>：マイルドな効果</li>
                  </ul>
                </div>
              </div>

              {/* 目薬 */}
              <div className="bg-purple-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-purple-800 mb-4">
                  <i className="ri-eye-line text-purple-600 mr-2"></i>
                  抗アレルギー目薬
                </h3>
                <p className="text-gray-700 mb-4">目のかゆみ・充血に効果的</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-semibold text-gray-800 mb-2">処方薬レベル</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• アレジオン点眼液</li>
                      <li>• ザジテン点眼液</li>
                      <li>• 強い症状に効果的</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-semibold text-gray-800 mb-2">マイルドタイプ</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• ロートアルガード</li>
                      <li>• アイボンAL</li>
                      <li>• 軽症から中等症に</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ケア方法 */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-400 pb-2">
              <i className="ri-shield-line text-orange-500 mr-2"></i>
              秋の花粉症対策・ケア方法
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* 外出時の対策 */}
              <div className="bg-yellow-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-yellow-800 mb-4">
                  <i className="ri-walk-line text-yellow-600 mr-2"></i>
                  外出時の対策
                </h3>
                <ul className="text-gray-700 space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>河川敷や空き地を避けて歩く</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>マスクとメガネを着用</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>風の強い日は外出を控える</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-yellow-600 mr-2 mt-1"></i>
                    <span>帰宅時は玄関で花粉を落とす</span>
                  </li>
                </ul>
              </div>

              {/* 室内での対策 */}
              <div className="bg-cyan-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-cyan-800 mb-4">
                  <i className="ri-home-line text-cyan-600 mr-2"></i>
                  室内での対策
                </h3>
                <ul className="text-gray-700 space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-cyan-600 mr-2 mt-1"></i>
                    <span>空気清浄機を活用する</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-cyan-600 mr-2 mt-1"></i>
                    <span>洗濯物は室内干しに</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-cyan-600 mr-2 mt-1"></i>
                    <span>窓の開閉は最小限にする</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-line text-cyan-600 mr-2 mt-1"></i>
                    <span>こまめに掃除機をかける</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 生活習慣 */}
            <div className="bg-pink-50 p-6 rounded-lg mt-8">
              <h3 className="text-xl font-semibold text-pink-800 mb-4">
                <i className="ri-heart-line text-pink-600 mr-2"></i>
                症状軽減のための生活習慣
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">食事面</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• ビタミンCを多く摂取</li>
                    <li>• 乳酸菌で腸内環境改善</li>
                    <li>• 抗炎症作用のある食材</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">睡眠・ストレス</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 十分な睡眠時間確保</li>
                    <li>• ストレス軽減を心がける</li>
                    <li>• 規則正しい生活リズム</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">その他</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• 適度な運動を継続</li>
                    <li>• 禁煙・節酒</li>
                    <li>• 保湿を心がける</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 薬剤師からのアドバイス */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-400 pb-2">
              <i className="ri-user-heart-line text-orange-500 mr-2"></i>
              薬剤師からのアドバイス
            </h2>
            
            <div className="bg-gradient-to-r from-orange-50 to-red-50 p-8 rounded-lg border-l-4 border-orange-400">
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-lightbulb-line text-orange-500 mr-3 mt-1 text-xl"></i>
                  <p className="text-gray-700">
                    <strong>早めの対策が重要：</strong>秋の花粉症は8月下旬から始まることが多いため、症状が出る前から薬の服用を開始することをおすすめします。
                  </p>
                </div>
                <div className="flex items-start">
                  <i className="ri-medicine-bottle-line text-orange-500 mr-3 mt-1 text-xl"></i>
                  <p className="text-gray-700">
                    <strong>薬の使い分け：</strong>鼻症状が強い場合は抗ヒスタミン薬、目症状が強い場合は点眼薬を中心に選択しましょう。
                  </p>
                </div>
                <div className="flex items-start">
                  <i className="ri-calendar-check-line text-orange-500 mr-3 mt-1 text-xl"></i>
                  <p className="text-gray-700">
                    <strong>継続的な服用：</strong>症状が軽くなっても、花粉シーズン中は薬の服用を継続することで、症状の悪化を防げます。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-400 pb-2">
              <i className="ri-checkbox-circle-line text-orange-500 mr-2"></i>
              まとめ
            </h2>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed mb-4">
                秋の花粉症は春とは異なる特徴を持ちますが、適切な薬の選択と生活習慣の改善により、症状を大幅に軽減することができます。
              </p>
              <p className="text-gray-700 leading-relaxed">
                症状でお困りの際は、お気軽に薬局までご相談ください。一人ひとりの症状に合わせた最適な治療法をご提案いたします。
              </p>
            </div>
          </section>

          {/* 関連記事 */}
          <section className="bg-orange-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              <i className="ri-links-line text-orange-500 mr-2"></i>
              関連記事
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <a href="/beast-blog/otc-selection" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h4 className="font-medium text-gray-800 mb-2">市販薬の正しい選び方</h4>
                <p className="text-sm text-gray-600">症状に合わせた市販薬選択のポイント</p>
              </a>
              <a href="/beast-blog/drug-food-interactions" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <h4 className="font-medium text-gray-800 mb-2">薬と食べ物の相互作用</h4>
                <p className="text-sm text-gray-600">注意すべき組み合わせについて</p>
              </a>
            </div>
          </section>
        </article>
      </main>

      {/* フッター */}
      <footer className="bg-gray-50 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600">
            この記事は薬剤師が監修しています。お薬に関するご相談は、お近くの薬局までお気軽にどうぞ。
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AutumnHayFeverGuidePage;
